import { useEffect, useState } from "react";

const STORAGE_KEY = "weakPasswordUid";
const CHANGE_EVENT = "weakPasswordChange";

// NOTE Firebase never gives the password back after sign-in, so the sign-in screen checks it against the password
// policy while it has it and leaves the result here. It's keyed on the user so a later sign-in by another user in
// the same tab doesn't inherit it. Storage can throw (private windows, blocked site data), then no banner is shown.
const read = () => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * Remembers for this browser session that the user signed in with a password that doesn't meet the current policy.
 * @param {string} uid
 * @param {boolean} isWeak false clears the mark
 */
export const setWeakPassword = (uid, isWeak) => {
  try {
    if (isWeak) window.sessionStorage.setItem(STORAGE_KEY, uid);
    else if (read() === uid) window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do, see above
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

/**
 * Forgets the mark regardless of user, for sign-out. NOTE the mark outlives a sign-out reload since it is stored per
 * tab, so without this a later sign-in with email link or SSO as the same user would show the banner.
 */
export const clearWeakPassword = () => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do, see above
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

/**
 * @param {string | undefined} uid
 * @returns {boolean} true if the user signed in with a weak password this session and hasn't changed it since
 */
export const useHasWeakPassword = uid => {
  const [stored, setStored] = useState(read);
  useEffect(() => {
    const update = () => setStored(read());
    window.addEventListener(CHANGE_EVENT, update);
    return () => window.removeEventListener(CHANGE_EVENT, update);
  }, []);
  return Boolean(uid) && stored === uid;
};
